import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const FullScreenZoomModal = ({
  open,
  onClose,
  images = [],
  currentIndex = 0,
  onIndexChange,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const containerRef = useRef(null);
  const dragMovedRef = useRef(false);

  // Reset zoom & pan when image changes or modal opens
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setZoomOrigin({ x: 50, y: 50 });
  }, [currentIndex, open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === "+" || e.key === "=") {
        handleZoomIn();
      } else if (e.key === "-") {
        handleZoomOut();
      } else if (e.key === "0") {
        handleResetZoom();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentIndex, images.length, scale]);

  if (!images || images.length === 0) return null;

  const currentItem = images[currentIndex];
  const currentUrl =
    typeof currentItem === "string" ? currentItem : currentItem?.url || "";
  const currentTitle =
    (typeof currentItem === "object" && currentItem?.title) ||
    `Product Image ${currentIndex + 1}`;

  const handleNext = () => {
    if (images.length <= 1) return;
    const nextIdx = (currentIndex + 1) % images.length;
    if (onIndexChange) onIndexChange(nextIdx);
  };

  const handlePrev = () => {
    if (images.length <= 1) return;
    const prevIdx = (currentIndex - 1 + images.length) % images.length;
    if (onIndexChange) onIndexChange(prevIdx);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const next = Math.max(prev - 0.5, 1);
      if (next === 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };

  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setZoomOrigin({ x: 50, y: 50 });
  };

  // Drag pan handlers
  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    dragMovedRef.current = false;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1) return;
    const nextPosition = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    };
    if (
      Math.abs(nextPosition.x - position.x) > 3 ||
      Math.abs(nextPosition.y - position.y) > 3
    ) {
      dragMovedRef.current = true;
    }
    setPosition(nextPosition);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleImageClick = (e) => {
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return;
    }

    if (scale > 1) {
      handleResetZoom();
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setZoomOrigin({ x, y });
    setScale(2.5);
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "rgba(10, 15, 20, 0.96)",
          backdropFilter: "blur(12px)",
          color: "#ffffff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Top Controls Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          zIndex: 10,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#ffffff", fontSize: "0.95rem" }}
          >
            {images.length > 1
              ? `${currentIndex + 1} of ${images.length}`
              : "Full Screen Zoom"}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.6)", display: { xs: "none", sm: "block" } }}
          >
            • Click image to zoom • Drag to pan
          </Typography>
        </Box>

        {/* Zoom & Action Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Zoom in (+)">
            <span>
              <IconButton
                aria-label="Zoom in"
                onClick={handleZoomIn}
                disabled={scale >= 4}
                sx={{
                  color: "#ffffff",
                  "&:disabled": { color: "rgba(255, 255, 255, 0.3)" },
                }}
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              minWidth: 42,
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.85)",
            }}
          >
            {Math.round(scale * 100)}%
          </Typography>

          <Tooltip title="Zoom out (-)">
            <span>
              <IconButton
                aria-label="Zoom out"
                onClick={handleZoomOut}
                disabled={scale <= 1}
                sx={{
                  color: "#ffffff",
                  "&:disabled": { color: "rgba(255, 255, 255, 0.3)" },
                }}
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Reset zoom (0)">
            <span>
              <IconButton
                aria-label="Reset zoom"
                onClick={handleResetZoom}
                disabled={scale === 1 && position.x === 0 && position.y === 0}
                sx={{
                  color: "#ffffff",
                  "&:disabled": { color: "rgba(255, 255, 255, 0.3)" },
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Close (Esc)">
            <IconButton
              aria-label="Close"
              onClick={onClose}
              sx={{
                color: "#ffffff",
                ml: 1,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Image Viewport */}
      <Box
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        sx={{
          flexGrow: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
          userSelect: "none",
        }}
      >
        {/* Previous Image Arrow Button */}
        {images.length > 1 && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            sx={{
              position: "absolute",
              left: { xs: 10, sm: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              color: "#ffffff",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              zIndex: 10,
              width: 48,
              height: 48,
              "&:hover": { backgroundColor: "rgba(7, 94, 84, 0.85)" },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 32 }} />
          </IconButton>
        )}

        {/* Display Image */}
        <Box
          component="img"
          src={currentUrl}
          alt={currentTitle}
          onClick={handleImageClick}
          sx={{
            maxWidth: "90vw",
            maxHeight: "75vh",
            objectFit: "contain",
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
            transition: isDragging ? "none" : "transform 0.2s cubic-bezier(0.2, 0, 0.2, 1)",
            pointerEvents: "auto",
            borderRadius: "8px",
          }}
        />

        {/* Next Image Arrow Button */}
        {images.length > 1 && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            sx={{
              position: "absolute",
              right: { xs: 10, sm: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              color: "#ffffff",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              zIndex: 10,
              width: 48,
              height: 48,
              "&:hover": { backgroundColor: "rgba(7, 94, 84, 0.85)" },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 32 }} />
          </IconButton>
        )}
      </Box>

      {/* Bottom Thumbnail Strip (if multiple images) */}
      {images.length > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            py: 1.5,
            px: { xs: 1.5, sm: 2.5 },
            borderTop: "1px solid rgba(255, 255, 255, 0.12)",
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(8px)",
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
            "&::-webkit-scrollbar": {
              height: 5,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              borderRadius: 10,
            },
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              margin: "0 auto",
              width: "max-content",
            }}
          >
            {images.map((item, idx) => {
              const url = typeof item === "string" ? item : item?.url || "";
              const isSelected = idx === currentIndex;

              return (
                <Box
                  key={idx}
                  onClick={() => {
                    if (onIndexChange) onIndexChange(idx);
                  }}
                  sx={{
                    width: { xs: 54, sm: 60 },
                    height: { xs: 54, sm: 60 },
                    flexShrink: 0,
                    borderRadius: "10px",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: isSelected
                      ? "2.5px solid #075e54"
                      : "1.5px solid rgba(255, 255, 255, 0.25)",
                    opacity: isSelected ? 1 : 0.65,
                    transform: isSelected ? "scale(1.06)" : "scale(1)",
                    transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                    backgroundColor: "#ffffff",
                    boxShadow: isSelected
                      ? "0 0 12px rgba(7, 94, 84, 0.6)"
                      : "none",
                    "&:hover": {
                      opacity: 1,
                      borderColor: "#075e54",
                      transform: "scale(1.06)",
                    },
                  }}
                >
                  <img
                    src={url}
                    alt={`Thumbnail ${idx + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                      backgroundColor: "#f8fafc",
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default FullScreenZoomModal;
